import './App.css'

import '@tribeplatform/react-components/styles'
import '@tribeplatform/react-ui-kit/styles'

import { Helmet } from 'react-helmet'

import {
  ClientError,
  ErrorResponse,
  GraphqlClient,
} from '@tribeplatform/gql-client'
import {
  CommandBar,
  CommandBarProvider,
} from '@tribeplatform/react-components/CommandBar'
import { I18nProvider } from '@tribeplatform/react-components/i18n'
import { Hydrate, Provider } from '@tribeplatform/react-sdk'
import { getNetworkKey } from '@tribeplatform/react-sdk/utils/keys'
import { AppProvider } from '@tribeplatform/react-ui-kit/AppProvider'

import { AppInitialProps } from './@types'
import { CacheControl } from './CacheControl'
import { GenericMeta } from './components/GenericMeta'
import { RuntimeConfigs } from './config'
import { useTracker } from './hooks/useTracker'
import { useInteractions, interactionEmitter } from './interactions'
import { initEmojiLib } from './lib/emoji.lib'
import { Intercom } from './lib/intercom/provider'
import { Optimizely } from './lib/optimizely/Provider'
import { TribeEmitter, TribeEmitterEvent } from './lib/tribeEvents/TribeEmitter'
import { Routes } from './Routes'
import { AppearanceProvider } from './themes/AppearanceProvider'
import { ThemeProvider } from './themes/ThemeProvider.component'
import { TribeWeb } from './TribeWeb'
import { onClientError, errorBoundaryHandler } from './utils/onError'
import { localeToDirection } from './utils/translation/localeToDirection'

export const App = ({
  pageProps,
  apiUrl,
}: {
  pageProps: AppInitialProps
  apiUrl: string
}) => {
  const locale = pageProps.authToken?.member.locale || 'en'
  const localeDirection = localeToDirection(locale)
  const tracker = useTracker({
    member: pageProps.authToken?.member,
    network: pageProps.authToken?.network,
  })
  initEmojiLib()

  return (
    <Provider
      config={{
        baseUrl: apiUrl,
        gatewayCacheControl: pageProps.bypassCache ? 'no-cache' : undefined,
        networkDomain: pageProps.authToken?.network?.domain,
        accessToken: pageProps.authToken?.accessToken,
        giphyApiKey: RuntimeConfigs.GIPHY_API_KEY,
        unsplashApiKey: RuntimeConfigs.UNSPLASH_ACCESS_KEY,
        interactionsContext: {
          useInteractions,
          interactionEmitter,
        },
        onError: (
          errors: ErrorResponse[] | undefined | null,
          client: GraphqlClient,
          error?: ClientError,
        ) => {
          if (!errors) {
            return
          }
          const nonNetworkRelatedErrors = errors
            .filter(
              _error => typeof _error !== undefined && typeof _error !== null,
            )
            .filter(_error => !_error.code)
          onClientError(nonNetworkRelatedErrors, client, pageProps, error)
        },
        onEvent: event => {
          const networkQueryHash = JSON.stringify(
            getNetworkKey({ fields: 'default' }),
          )
          const networkCachedQuery = pageProps.dehydratedState.queries.find(
            query => JSON.stringify(query.queryKey) === networkQueryHash,
          )
          const {
            state: { data: network },
          } = networkCachedQuery

          TribeEmitter.emit(TribeEmitterEvent.Event, { ...event, network })
        },
        errorBoundary: {
          handler: errorBoundaryHandler,
        },
        tracker,
      }}
    >
      {pageProps.bypassCache && <CacheControl />}
      <I18nProvider i18n={pageProps.i18n}>
        <Helmet>
          <html lang={locale} dir={localeDirection} />
        </Helmet>
        <Hydrate state={pageProps.dehydratedState}>
          <Optimizely datafile={pageProps.optimizelyDataFile}>
            <GenericMeta pageProps={pageProps} />
            <Intercom authToken={pageProps.authToken}>
              <AppearanceProvider appearance={pageProps.appearance}>
                <ThemeProvider>
                  <AppProvider>
                    <TribeWeb pageProps={pageProps}>
                      <CommandBarProvider>
                        <CommandBar />
                        <Routes pageProps={pageProps} />
                      </CommandBarProvider>
                    </TribeWeb>
                  </AppProvider>
                </ThemeProvider>
              </AppearanceProvider>
            </Intercom>
          </Optimizely>
        </Hydrate>
      </I18nProvider>
    </Provider>
  )
}
